import React from 'react';

import { Box, Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAudioBriefing } from '../../../hooks/useAudioBriefing';
import { PlayFromBeginningButton } from '../../controls/PlayFromBeginningButton';
import { PlayPauseButton } from '../../controls/PlayPauseButton';
import { SkipBackButton } from '../../controls/SkipBackButton';
import { SkipForwardButton } from '../../controls/SkipForwardButton';
import { PlaybackSpeedControl } from '../../controls/PlaybackSpeedControl';
import { AudioTimeIndicator } from '../../controls/AudioTimeIndicator';
import { getSharedAttributes } from '../../../utils/getSharedAttributes';
import { AudioFeature } from '../../../types/audioFeatureType';

export type AudioNarrationPanelProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	handleFetchContent: () => void;
	prefetchLabel?: string;
	loadingLabel?: string;
};

const styles = cssMap({
	wrapper: {
		flex: 1,
	},
	outerControlButtonWrapper: {
		// This is the width of the speed option that requires the most space
		// Ensures that the displayed speed is never truncated and that spacing remains uniform
		width: '54px',
	},
	controlsWrapper: {
		paddingBlock: token('space.100'),
	},
});

export const AudioNarrationPanel = ({
	audioRef,
	handleFetchContent,
	prefetchLabel,
	loadingLabel,
}: AudioNarrationPanelProps) => {
	const [{ activeFeature, isPlaying, isLoading, hasFetched, playbackSpeed }] = useAudioBriefing();

	const { trackAIResultAction } = useAIEventsInstrumentation();

	const isFeatureActive = activeFeature === AudioFeature.NARRATION;
	const showExtraControls = isFeatureActive && hasFetched && !isLoading;

	const isFeatureLoading = isFeatureActive && isLoading;
	const isFeatureFetched = isFeatureActive && hasFetched;
	const isFeaturePlaying = isFeatureActive && isPlaying;

	const sharedAttributes = getSharedAttributes({
		playbackSpeed,
		hasFetched,
	});

	const handlePlayFromBeginningButtonClick = () => {
		trackAIResultAction('audioPlayFromBeginning', {
			attributes: sharedAttributes,
		});
	};

	const handlePlayPauseButtonClick = () => {
		if (isPlaying) {
			trackAIResultAction('audioPause', {
				attributes: sharedAttributes,
			});
		} else {
			trackAIResultAction('audioPlay', {
				attributes: sharedAttributes,
			});
		}
	};

	const handleSkipBackButtonClick = () => {
		trackAIResultAction('audioSkipBackward', {
			attributes: sharedAttributes,
		});
	};

	const handleSkipForwardButtonClick = () => {
		trackAIResultAction('audioSkipForward', {
			attributes: sharedAttributes,
		});
	};

	const handlePlaybackSpeedButtonClick = (speed: number) => {
		trackAIResultAction('audioPlaybackSpeed', {
			attributes: { ...sharedAttributes, playbackSpeed: speed },
		});
	};

	return (
		<Box xcss={styles.wrapper} testId="listen-panel-controls">
			<Stack space="space.150" alignInline="center" xcss={styles.controlsWrapper}>
				<Inline space="space.150" alignBlock="center">
					{showExtraControls && (
						<>
							<Flex xcss={styles.outerControlButtonWrapper} justifyContent="center">
								<PlayFromBeginningButton
									audioRef={audioRef}
									onClick={handlePlayFromBeginningButtonClick}
								/>
							</Flex>
							<SkipBackButton audioRef={audioRef} onClick={handleSkipBackButtonClick} />
						</>
					)}

					<PlayPauseButton
						audioRef={audioRef}
						isPlaying={isFeaturePlaying}
						isLoading={isFeatureLoading}
						hasFetched={isFeatureFetched}
						fetchContent={handleFetchContent}
						onClick={handlePlayPauseButtonClick}
					/>

					{showExtraControls && (
						<>
							<SkipForwardButton audioRef={audioRef} onClick={handleSkipForwardButtonClick} />
							<Box xcss={styles.outerControlButtonWrapper}>
								<PlaybackSpeedControl
									audioRef={audioRef}
									onClick={handlePlaybackSpeedButtonClick}
								/>
							</Box>
						</>
					)}
				</Inline>
				<AudioTimeIndicator
					audioRef={audioRef}
					isLoading={isFeatureLoading}
					hasFetched={isFeatureFetched}
					prefetchLabel={prefetchLabel}
					loadingLabel={loadingLabel}
				/>
			</Stack>
		</Box>
	);
};
